/* Reconciliation — "How It Works" Explainer (STA-6 · Feb 2026)
   ==================================================================
   Per-check documentation card. Explains in Arabic:
     • What each of the 6 checks compares (formula)
     • Where the two sides come from (data source)
     • When a variance is REAL vs. a KNOWN FALSE-POSITIVE cause
     • What the classification (color) means

   Collapsible — always visible header, expandable body. */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, BookOpen } from 'lucide-react';

const CHECKS = [
  {
    name: 'Revenue ↔ Invoices',
    formula: 'GL 41xxx (net credit) − Σ subtotal لكل فاتورة معتمدة تاريخها ضمن الفترة',
    sub_source: 'فواتير مبيعات approved غير مُلغاة (invoice_date ضمن الفترة)',
    gl_source: 'حركة الحسابات ذات account_type=revenue خلال الفترة',
    real_error: 'فاتورة معتمدة موجودة لكن قيدها لم يُرحَّل → variance سالب',
    false_positive: 'فاتورة أُدخلت في يناير لكن تاريخها فبراير — تم إصلاحه باستخدام invoice_date',
  },
  {
    name: 'AR ↔ Customer Balances',
    formula: 'GL 12xxx (net debit) − Σ amount_remaining لكل فاتورة مفتوحة',
    sub_source: 'الفواتير المعتمدة غير المُلغاة، بشرط أن تاريخها ≤ نهاية الفترة',
    gl_source: 'حركة الحسابات التي تبدأ بـ 12 أو system_code=ACCOUNTS_RECEIVABLE',
    real_error: 'فاتورة مفتوحة بلا قيد → GL AR أقل من sub-ledger',
    false_positive: 'فواتير مستقبلية (تاريخها بعد نهاية الفترة) — تم استثناؤها',
  },
  {
    name: 'Cash ↔ Payments',
    formula: 'GL Cash (Registry) − (Σ received − Σ made) − Σ bank_adjustments',
    sub_source: 'المدفوعات المعتمدة + التسويات البنكية (رسوم/فوائد/تصحيحات)',
    gl_source: 'حسابات مُسجَّلة في CashAccountRegistry فقط (لا heuristics)',
    real_error: 'مدفوعة معتمدة بدون قيد، أو قيد نقدي بدون مدفوعة/تسوية',
    false_positive: 'Cash Registry فارغ → الآن يعطي NOT_CONFIGURED بدلاً من FAIL',
  },
  {
    name: 'VAT Ledger ↔ VAT Returns',
    formula: 'GL VAT (11300 debit − 21300 credit) vs. Return snapshot (Output − Input)',
    sub_source: 'الإقرار المُقدَّم (snapshot) — يشمل فواتير مورّدين + نفقات + تسويات',
    gl_source: 'حركة حسابات VAT في القيود (11300/21300/21310)',
    real_error: 'فاتورة بها vat_amount لكن قيدها بلا سطر VAT_INPUT — كشفناها في STA-5',
    false_positive: 'قيود reversed تدخل في GL لكن ليس في الإقرار — تُصنَّف كـ timing (طبيعي)',
  },
  {
    name: 'Project Profit ↔ General Ledger',
    formula: 'Σ per-project (revenue − 51 − 53) − (GL revenue − GL 51 − GL 53)',
    sub_source: 'مجموع P&L لكل مشروع مُسجَّل',
    gl_source: 'GL على مستوى الشركة كاملة',
    real_error: 'قيود 51xxx (تكاليف مباشرة) بدون project_id',
    false_positive: 'الفرق يُعتبر signal داخلي وليس خطأ محاسبياً — لا يرتفع لـ FAIL أبداً',
  },
  {
    name: 'Overhead Allocation ↔ 53xxx',
    formula: 'Σ overhead_scenarios (posted + approved) − GL 53xxx (net debit)',
    sub_source: 'سيناريوهات المصاريف العامة في حالتَي posted و approved',
    gl_source: 'حركة حسابات 53xxx',
    real_error: 'حركة GL 53 بلا سيناريو مقابل، أو سيناريو معتمد لم يُرحَّل',
    false_positive: 'كنا نستبعد approved — الآن يُحسب لتفادي WARNING كاذب',
  },
];

const VC_LEGEND = [
  { code: 'none',              color: 'bg-emerald-100 text-emerald-800', ar: 'لا يوجد فرق' },
  { code: 'timing',            color: 'bg-emerald-100 text-emerald-800', ar: 'فرق توقيت طبيعي' },
  { code: 'config_missing',    color: 'bg-amber-100 text-amber-800',    ar: 'إعداد ناقص' },
  { code: 'missing_posting',   color: 'bg-orange-100 text-orange-800',  ar: 'قيد محاسبي مفقود' },
  { code: 'data_integrity',    color: 'bg-rose-100 text-rose-800',      ar: 'خلل في سلامة البيانات' },
  { code: 'manual_adjustment', color: 'bg-sky-100 text-sky-800',        ar: 'قيد يدوي خارج النظام' },
  { code: 'unknown',           color: 'bg-slate-200 text-slate-700',    ar: 'غير محدد' },
];

export default function HowItWorksPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden"
         data-testid="how-it-works-panel">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left"
        data-testid="how-it-works-toggle"
      >
        <div className="flex items-center gap-2">
          <BookOpen size={16} className="text-indigo-600" />
          <div>
            <div className="text-sm font-bold text-slate-900">How It Works — شرح آلية الفحوصات الستة</div>
            <div className="text-[11px] text-slate-500">
              معادلة كل فحص، مصدر البيانات، متى يكون الخطأ حقيقياً ومتى يكون كاذباً.
            </div>
          </div>
        </div>
        {open ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
      </button>
      {open && (
        <div className="px-4 py-3 border-t border-slate-100 space-y-3">
          {CHECKS.map(c => (
            <div key={c.name} className="bg-slate-50 border border-slate-200 rounded p-3"
                 data-testid={`how-check-${c.name}`}>
              <div className="text-sm font-bold text-slate-900 mb-2">{c.name}</div>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                <div><dt className="inline font-bold text-slate-700">المعادلة: </dt><dd className="inline font-mono text-[10px]">{c.formula}</dd></div>
                <div><dt className="inline font-bold text-slate-700">مصدر Sub-Ledger: </dt><dd className="inline">{c.sub_source}</dd></div>
                <div><dt className="inline font-bold text-slate-700">مصدر GL: </dt><dd className="inline">{c.gl_source}</dd></div>
                <div><dt className="inline font-bold text-rose-700">خطأ حقيقي: </dt><dd className="inline">{c.real_error}</dd></div>
                <div className="md:col-span-2"><dt className="inline font-bold text-emerald-700">خطأ كاذب مُعالَج: </dt><dd className="inline">{c.false_positive}</dd></div>
              </dl>
            </div>
          ))}

          <div className="bg-indigo-50 border border-indigo-200 rounded p-3"
               data-testid="variance-class-legend">
            <div className="text-sm font-bold text-indigo-900 mb-2">تصنيف سبب الفرق (Variance Classification)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {VC_LEGEND.map(v => (
                <div key={v.code} className={`rounded px-2 py-1.5 text-[10px] ${v.color}`}>
                  <div className="font-bold">{v.ar}</div>
                  <div className="opacity-70 font-mono text-[9px]">{v.code}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
            <div className="text-sm font-bold text-emerald-900 mb-1">Confidence Score</div>
            <div className="text-[11px] text-slate-700 leading-relaxed">
              كل فحص يعرض نسبة موثوقية 0-100% مع أسباب واضحة (مثلاً: -20 لأن Cash Registry فارغ).
              يُساعدك على تمييز نتيجة PASS موثوقة (95%+) من نتيجة PASS ضعيفة الأساس (30%).
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
